import React from "react";
import dayjs from "dayjs";
import { useFunnelNavigate } from "src/common/hooks/useFunnelNavigate.hook";
import { CustomerInterface, i18n, saveCustomer, setBListToggle, setGlobalLoading, setSoIdWeb, showMessage, useAppDispatch, useCreateSoWithCaptcha, useForm } from "src/common";
import { createSoSupplementary } from "src/common/api/apollo/supplementary";
import { Button, CaptchaChallenge, Flex, InputBirthDate, InputPhoneNumber, InputText, RadioButtonGroupInput } from "src/components";
import { AppRoutes } from "src/pages/app";
import { FieldConfig, Form, FormConfig } from "./form";
import { Validation } from "./validation";

export type CustomerPolicyComparisonFormItemType = 'birthDate' | 'email' | 'phoneNumber' | 'insurancePrice' | 'hasBList';
export const allCustomerPolicyComparisonFormItems: Array<CustomerPolicyComparisonFormItemType> = ['birthDate', 'email', 'phoneNumber', 'insurancePrice', 'hasBList'];

interface CustomerPolicyComparisonFormProps {
  customer?: CustomerInterface;
  formItems?: Array<CustomerPolicyComparisonFormItemType>;
  labels?: Partial<Record<CustomerPolicyComparisonFormItemType, string>>;
  buttonLabel?: string;
}

// Primjer korištenja komponente forme
export const CustomerPolicyComparisonForm: React.FC<CustomerPolicyComparisonFormProps> = ({ customer, formItems = allCustomerPolicyComparisonFormItems, labels, buttonLabel }) => {
  const { formRef, submitForm } = useForm();
  const dispatch = useAppDispatch();
  const navigate = useFunnelNavigate();

  // Same captcha-aware createSo as CustomerForm — see the note there.
  const { submit: createSo, captcha } = useCreateSoWithCaptcha(createSoSupplementary, { page: 'dzo-policy-comparison' });

  const handleSubmit = (values: { [key: string]: any }) => {
    dispatch(setGlobalLoading(true));
    const data: CustomerInterface = {
      ...values,
      age: dayjs().diff(dayjs(values.birthDate), 'year')
    }
    createSo(
      {
        input: {
          dateOfBirth: data.birthDate,
          email: data.email,
          phoneNumber: data.phoneNumber,
          productTypeCode: 'dzo',
          source: 'compDZOfunnel',
          diffPrice: Number(String(values.insurancePrice).trim().replace(',', '.')),
          hasBList: values.hasBList
        }
      },
      {
        onSuccess: (response) => {
          const soId = response.data.createSo;
          dispatch(saveCustomer(data));
          dispatch(setSoIdWeb(soId));
          // backend filters offers by hasBList from the SO; keep the client-side
          // B-list toggle in sync so it doesn't hide the returned offers
          dispatch(setBListToggle(values.hasBList === true));
          navigate(AppRoutes.OFFERS.fullPath);
        },
        onError: (error) => {
          console.error('Greška prilikom izvršavanja mutacije:', error);
          // Previously a silent dead-end (console only). Rate-limit denials already
          // toast via the Apollo error link; everything else gets the generic message.
          const isRateLimited = error?.graphQLErrors?.some(
            (gqlError: { extensions?: { code?: string } }) =>
              gqlError.extensions?.code === 'RATE_LIMITED' || gqlError.extensions?.code === 'IP_BLOCKED'
          );
          if (!isRateLimited) {
            dispatch(showMessage({ message: i18n.t('common.globalError'), type: 'error', duration: 8000 }));
          }
        },
        onSettled: () => {
          dispatch(setGlobalLoading(false));
        },
      },
    );
  };

  const fields: FieldConfig[] = [
    {
      name: 'birthDate',
      label: labels?.birthDate ?? i18n.t('components.forms.customerPolicyComparisonForm.dateOfBirth'),
      component: InputBirthDate,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validDate', message: 'Unesi ispravan datum rođenja' },
      ] as Validation[],
    },
    {
      name: 'insurancePrice',
      label: labels?.insurancePrice ?? i18n.t('components.forms.customerPolicyComparisonForm.insurancePrice'),
      component: InputText,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validInsurancePrice', message: 'Unesi cijenu osiguranja veću od 0' },
      ] as Validation[],
    },
    {
      name: 'hasBList',
      label: labels?.hasBList ?? i18n.t('components.forms.customerPolicyComparisonForm.hasBList'),
      component: RadioButtonGroupInput,
      componentProps: {
        options: [{ label: 'Da', value: true }, { label: 'Ne', value: false }],
        horizontal: false,
      }
    },
    {
      name: 'email',
      label: labels?.email ?? i18n.t('components.forms.customerPolicyComparisonForm.mail'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50
      },
      validations: [
        { type: 'required', message: 'Polje je obavezno.' },
        { type: 'validMail', message: 'Unesite ispravnu e-mail adresu.' },
        { type: 'validMailNoDiacritics', message: 'Dio e-mail adrese prije @ ne smije sadržavati č, ć, ž, š ni đ.' },
      ] as Validation[],
    },
    {
      name: 'phoneNumber',
      label: labels?.phoneNumber ?? i18n.t('components.forms.customerPolicyComparisonForm.phone'),
      component: InputPhoneNumber,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validPhoneNumber', message: 'Unesi ispravan broj mobitela', params: { regionCode: 'countryCode' } },
      ] as Validation[],
    }
  ].filter((field) => formItems.includes(field.name as CustomerPolicyComparisonFormItemType));

  const formConfig: FormConfig = {
    gap: 16
  };

  return (
    <Flex vertical gap={44}>
      {/* hasBList seeded to match the radio's preselected 'Da' — the Form only submits
          values present in its state, so an untouched radio would otherwise submit undefined */}
      <Form ref={formRef} fields={fields} config={formConfig} onSubmit={handleSubmit} initialValues={{ hasBList: true, ...customer }} noValidate />
      <CaptchaChallenge {...captcha} />
      <Button btnType="primary" iconPosition="right" text={buttonLabel ?? i18n.t('components.forms.customerPolicyComparisonForm.submit')} style={{ width: '100%' }} disabled={captcha.required} onClick={() => submitForm()}></Button>
    </Flex>
  )
};
